/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ProductFilterColumns,
  ProductFilterModel,
  useProductFilter,
  useSetProductFilter,
  useValidateProductListOrder,
} from '@gripp/shared-logic';
import {
  ColumnSelection,
  DropdownFooter,
  FilterType,
  FormContext,
  filterStyles as styles,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ScrollView, View } from 'react-native';
import { array, string, object as yupObject } from 'yup';

type ProductFilterProps = {
  onCloseSideBar: () => void;
  type: FilterType;
};

export const ProductFilter = (props: ProductFilterProps) => {
  const { data: productFilter } = useProductFilter();
  const { mutateAsync: setProductFilter } = useSetProductFilter();
  const validateListOrder = useValidateProductListOrder();

  const formSchema = yupObject({
    columns: array().of(string().optional().nullable()).optional().nullable(),
  });
  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      columns: productFilter?.columns,
    },
  });

  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
    watch,
  } = formMethods;

  useEffect(() => {
    reset({
      columns: productFilter?.columns,
    });
  }, [productFilter, reset]);

  const selectedColumns = watch('columns');

  const isColumnsFilter = props.type === FilterType.PRODUCTCOLUMNS;

  const resetForm = useCallback(() => {
    reset({
      ...selectedColumns,
      ...(isColumnsFilter && { columns: new ProductFilterModel().columns }),
    });
  }, [isColumnsFilter, reset, selectedColumns]);

  const canReset = (): boolean => {
    return !Object.values(getValues()).every((value) => value === undefined);
  };

  const applyFilter = async (filterModel: any) => {
    await Promise.all([
      setProductFilter(filterModel),
      validateListOrder(filterModel),
    ]);

    props.onCloseSideBar();
  };

  const onReset = async () => {
    const model = new ProductFilterModel();
    const updatedFields = {
      ...(isColumnsFilter && { columns: model.columns }),
    };

    resetForm();

    const mergedFilters = { ...productFilter, ...updatedFields };
    await applyFilter(new ProductFilterModel(mergedFilters));
  };

  const onSubmit = async (data: any) => {
    const model = new ProductFilterModel({
      ...productFilter,
      ...data,
    });
    await applyFilter(model);
  };

  const renderStandardData = () => {
    return (
      <ColumnSelection
        baseColumns={ProductFilterColumns}
        i18nPrefix="product.filter"
      />
    );
  };

  return (
    <FormContext.Provider value={formMethods}>
      <View style={styles.container}>
        <View style={[styles.scrollViewContainer, { marginBottom: 30 }]}>
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            {isColumnsFilter && renderStandardData()}
          </ScrollView>
        </View>
        <DropdownFooter
          isDirty={isDirty}
          onSubmit={handleSubmit(onSubmit)}
          canReset={canReset}
          onReset={onReset}
        />
      </View>
    </FormContext.Provider>
  );
};
